import { getApplyUniList, partnerRegisterApplicant } from '@/store/apply/actions';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { BaseRegistrationFormSchema, IProviderRegistration, PartnerFormKeyNames, ProviderRegistrationSchema } from 'api-paths-cas';
import { z } from 'zod';
import { structureProgrammeChoicesObject } from '../../utils/programmeChoicesHelper';
import { initialPartnerFormValues, UseApplicationFormReturnType } from './types';
import { useProviderUniversities } from '@/utilities/hooks/useProviderUniversities';

// Extend the base schema but make programmeChoices optional and add UI fields
// We omit programmeChoices from the base schema and add it back as optional
const ProviderRegistrationFormSchema = ProviderRegistrationSchema.omit({
  programmeChoices: true,
}).extend(BaseRegistrationFormSchema);

// Infer the type from the schema
export type IProviderRegistrationForm = z.infer<typeof ProviderRegistrationFormSchema>;

export function usePartnerForm(isOpen: boolean): UseApplicationFormReturnType<IProviderRegistrationForm> {
  const dispatch = useDispatch();

  const form = useForm<IProviderRegistrationForm>({
    resolver: zodResolver(ProviderRegistrationFormSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: initialPartnerFormValues,
  });

  const { handleSubmit, reset, setValue } = form;

  // Reset form when drawer closes
  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const submitPartnerRegistration = (values: IProviderRegistrationForm) => {
    const programmeChoices = structureProgrammeChoicesObject({
      courseId: values[PartnerFormKeyNames.COURSE_ID],
      courseName: values[PartnerFormKeyNames.COURSE_NAME],
      intakeId: values[PartnerFormKeyNames.INTAKE_ID],
      studyLevel: values[PartnerFormKeyNames.STUDY_LEVEL],
      mode: values[PartnerFormKeyNames.MODE],
      campus: values[PartnerFormKeyNames.CAMPUS],
      entryPoint: values[PartnerFormKeyNames.ENTRY_POINT],
    });

    const registrationData: IProviderRegistration = {
      email: values[PartnerFormKeyNames.EMAIL]?.toLowerCase().trim(),
      preferredName: values[PartnerFormKeyNames.PREFERRED_NAME],
      universityId: values[PartnerFormKeyNames.UNIVERSITY_ID],
      nationality: values[PartnerFormKeyNames.NATIONALITY],
      dob: values[PartnerFormKeyNames.DOB],
      phoneNumber: values[PartnerFormKeyNames.MOBILE_NUMBER],
      feeStatus: values[PartnerFormKeyNames.FEE_STATUS],
      programmeChoices,
    };

    dispatch(partnerRegisterApplicant(registrationData));
  };

  const onSubmit = (values: IProviderRegistrationForm) => {
    submitPartnerRegistration(values);
  };

  const clearDependentFields = useCallback(
    (fields: string[]) => {
      for (const field of fields) {
        if (field === 'courseName') {
          setValue(PartnerFormKeyNames.COURSE_NAME, '', { shouldValidate: false });
        } else if (field === 'mode') {
          setValue(PartnerFormKeyNames.MODE, '', { shouldValidate: false });
        } else if (field === 'campus') {
          setValue(PartnerFormKeyNames.CAMPUS, '', { shouldValidate: false });
        } else if (field === 'entryPoint') {
          setValue(PartnerFormKeyNames.ENTRY_POINT, '', { shouldValidate: false });
        } else if (field === 'courseId') {
          setValue(PartnerFormKeyNames.COURSE_ID, '', { shouldValidate: false });
        } else {
          setValue(field as keyof IProviderRegistrationForm, '', { shouldValidate: false });
        }
      }
    },
    [setValue],
  );

  useEffect(() => {
    dispatch(getApplyUniList());
  }, [getApplyUniList, dispatch]);

  const { universities: availableUniversities } = useProviderUniversities();

  return {
    form,
    uniOptions: availableUniversities,
    onSubmit: handleSubmit(onSubmit),
    clearDependentFields,
  };
}
